import { API_BASE, authHeaders } from './apiAuth'

/**
 * The colours of the libraries' filter sheet on the black skin, as an
 * administrator last synced them (site setting 'filter-colors', 2026-10-02).
 *
 * The tuner that set them is gone (2026-10-02, with the light skin); the
 * colours it left are still read, so the black skin looks exactly as it did.
 * They are written into a stylesheet that applies only while the page is
 * dark -- the light skin has its own filter colours (theme.css), and the
 * synced ones must not leak into it.
 */

const NAME = /^--wf-[a-z0-9-]{1,32}$/
const VALUE = /^(#[0-9a-f]{6}|0(\.\d{1,4})?|1(\.0{1,4})?)$/i
const SETTING = 'filter-colors'

let loading: Promise<void> | null = null

function write(values: Record<string, string>) {
  const rules = Object.entries(values)
    .filter(([name, value]) => NAME.test(name) && VALUE.test(value))
    .map(([name, value]) => `${name}: ${value};`)
    .join(' ')
  if (!rules) return
  let sheet = document.getElementById('ww-filter-colors') as HTMLStyleElement | null
  if (!sheet) {
    sheet = document.createElement('style')
    sheet.id = 'ww-filter-colors'
    document.head.appendChild(sheet)
  }
  sheet.textContent = `:root:not([data-theme='light']) { ${rules} }`
}

/** Read the synced colours once per visit; web.css's defaults stand otherwise. */
export function loadFilterColors(): Promise<void> {
  if (!loading) {
    loading = (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/site-settings?key=${SETTING}`, {
          headers: { Accept: 'application/json', ...(await authHeaders()) },
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const body = (await response.json()) as { value?: unknown }
        if (body.value && typeof body.value === 'object' && !Array.isArray(body.value)) {
          write(body.value as Record<string, string>)
        }
      } catch {
        loading = null // try again on the next library page
      }
    })()
  }
  return loading
}
